<script setup lang="ts">
import { useAuthStore, useClassStore, useUserStore } from '../../stores';

import { computed } from 'vue';
import { UserRole } from '../../types/user';
import { ClassStatus, ClassViewMode } from '../../types/class';
import RequestJoinClassList from './RequestJoinClassList.vue';
import Settings from './Settings.vue';
import ViewMode from './ViewMode.vue';
import StudentLeaderboard from './StudentLeaderboard.vue';
import Logo from '../../assets/imgs/iclass-logo.svg';
import SessionList from './SessionList.vue';
import LockScreen from '../Control/LockScreen.vue';
import RaiseHandToggle from '../Control/RaiseHandToggle.vue';
import RaiseHandList from '../Control/RaiseHandList.vue';
import { TEXT } from '../../configs/text';
import { useI18n } from 'petite-vue-i18n';

const auth = useAuthStore()
const user = useUserStore()
const classStore = useClassStore()

const hasRequest = computed(() => {
    return user.userRole === UserRole.teacher && classStore.requestedStudentIds.length && classStore.selectedClassDetails
})

const sessionSwitchable = computed((): boolean => {
    const isTeacherManaging = user.userRole === UserRole.teacher && classStore.viewMode === ClassViewMode.teaching
    const isStudentIdle = user.userRole === UserRole.student && classStore.selectedClassDetails?.state === ClassStatus.on_going
    return isTeacherManaging || isStudentIdle
})

const raiseHandState = computed(() => classStore.selectedClassDetails?.allowRaiseHand || false)

const { t } = useI18n()

</script>

<template>
    <div class="relative w-full flex flex-wrap items-center gap-2 lg:gap-3 py-3 px-3 lg:px-8 bg-white z-20">
        <Logo class="hidden md:block w-28 shrink-0" />
        <div class="flex flex-wrap gap-3">
            <SessionList class="z-10 mb-1" :disabled="!sessionSwitchable" v-if="auth.isAuthenticated && classStore.selectedClassDetails" />

            <RaiseHandList
                v-if="auth.isAuthenticated && classStore.selectedClassDetails && user.userRole === UserRole.teacher && classStore.selectedClassDetails.state === ClassStatus.on_going && classStore.viewMode === ClassViewMode.teaching && raiseHandState" />

            <div class="flex flex-col gap-0 justify-center" :class="{ 'hidden': user.userRole === UserRole.teacher && classStore.viewMode === ClassViewMode.teaching }" v-if="auth.isAuthenticated">
                <p class="text-sm">{{ `${t(TEXT.hello)},` }} <span class="font-bold" v-if="user.userRole === UserRole.student">{{ user.userInfo?.full_name }}</span></p>
                <p class="font-bold" v-if="user.userRole === UserRole.teacher">{{ user.userInfo?.full_name }}</p>
                <p class="font-bold text-lime-500" v-if="user.userRole === UserRole.student &&
                    classStore.selectedClassDetails &&
                    classStore.selectedClassDetails.student_group_id &&
                    classStore.selectedClassDetails.state === ClassStatus.on_going">
                    {{ classStore.getClassGroupNameById(classStore.selectedClassDetails.student_group_id) }}
                </p>
            </div>
        </div>

        <div class="flex flex-wrap gap-3 justify-end ml-auto">

            <RequestJoinClassList class="ml-auto" v-if="classStore.selectedClassDetails && user.userRole === UserRole.teacher && hasRequest && classStore.viewMode === ClassViewMode.management" />

            <template v-if="classStore.selectedClassDetails && user.userRole === UserRole.teacher && classStore.viewMode === ClassViewMode.teaching && classStore.selectedClassDetails.state === ClassStatus.on_going">
                <LockScreen />
                <RaiseHandToggle />
            </template>

            <ViewMode v-if="classStore.selectedClassDetails && user.userRole === UserRole.teacher" />

            <StudentLeaderboard v-if="classStore.selectedClassDetails?.state === ClassStatus.on_going && user.userRole === UserRole.student" />

            <div class="flex items-center justify-center p-1.5 text-2xl rounded-md bg-gray-100" v-if="user.userRole === UserRole.teacher && auth.isAuthenticated && classStore.selectedClassDetails && classStore.selectedClassDetails.access_code">
                <span class="font-bold text-lime-500">
                    {{ classStore.selectedClassDetails.access_code }}
                </span>
            </div>

            <!-- TODO: cleanup -->
            <!-- <div class="flex items-center justify-center p-1.5 text-2xl rounded-md bg-gray-100"
                v-if="user.userRole === UserRole.student && auth.isAuthenticated && classStore.selectedClassDetails && classStore.selectedClassDetails.student_group_id && classStore.selectedClassDetails.state === ClassStatus.on_going">
                <span class="font-bold text-lime-500">
                    {{ classStore.getClassGroupNameById(classStore.selectedClassDetails.student_group_id) }}
                </span>
            </div> -->

            <Settings />

        </div>

    </div>
</template>